import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  SocialAutoReplyRule,
  CreateAutoReplyRuleRequest,
  UseAutoReplyRulesResult,
} from '@/types/social';
import { toast } from 'sonner';

export function useAutoReplyRules(businessId: string | null): UseAutoReplyRulesResult {
  const queryClient = useQueryClient();
  const queryKey = ['social-auto-reply-rules', businessId];

  // Fetch rules
  const {
    data: rules = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<SocialAutoReplyRule[]> => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('social_auto_reply_rules')
        .select('*')
        .eq('business_id', businessId)
        .order('priority', { ascending: true });

      if (error) throw error;
      return (data || []) as SocialAutoReplyRule[];
    },
    enabled: !!businessId,
  });

  // Create rule mutation
  const createRuleMutation = useMutation({
    mutationFn: async (request: CreateAutoReplyRuleRequest): Promise<SocialAutoReplyRule> => {
      const { data: user } = await supabase.auth.getUser();

      // Get max priority
      const { data: maxPriorityData } = await supabase
        .from('social_auto_reply_rules')
        .select('priority')
        .eq('business_id', request.business_id)
        .order('priority', { ascending: false })
        .limit(1)
        .single();

      const nextPriority = (maxPriorityData?.priority || 0) + 1;

      const { data, error } = await supabase
        .from('social_auto_reply_rules')
        .insert({
          business_id: request.business_id,
          name: request.name,
          trigger_type: request.trigger_type,
          trigger_config: request.trigger_config || {},
          platforms: request.platforms || ['instagram', 'facebook'],
          response_type: request.response_type || 'text',
          response_content: request.response_content,
          response_delay_seconds: request.response_delay_seconds || 0,
          max_triggers_per_contact_per_day: request.max_triggers_per_contact_per_day || 3,
          active_schedule: request.active_schedule || {},
          priority: nextPriority,
          is_active: true,
          created_by: user?.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as SocialAutoReplyRule;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Rule created', { description: 'Auto-reply rule has been created' });
    },
    onError: (error: Error) => {
      toast.error('Failed to create rule', { description: error.message });
    },
  });

  // Update rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: async ({
      ruleId,
      updates,
    }: {
      ruleId: string;
      updates: Partial<SocialAutoReplyRule>;
    }): Promise<SocialAutoReplyRule> => {
      // Remove fields that shouldn't be updated directly
      const { id, business_id, created_by, created_at, ...validUpdates } = updates as any;

      const { data, error } = await supabase
        .from('social_auto_reply_rules')
        .update({
          ...validUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', ruleId)
        .select()
        .single();

      if (error) throw error;
      return data as SocialAutoReplyRule;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Rule updated', { description: 'Auto-reply rule has been updated' });
    },
    onError: (error: Error) => {
      toast.error('Failed to update rule', { description: error.message });
    },
  });

  // Delete rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: string): Promise<void> => {
      const { error } = await supabase
        .from('social_auto_reply_rules')
        .delete()
        .eq('id', ruleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Rule deleted', { description: 'Auto-reply rule has been deleted' });
    },
    onError: (error: Error) => {
      toast.error('Failed to delete rule', { description: error.message });
    },
  });

  // Toggle rule mutation
  const toggleRuleMutation = useMutation({
    mutationFn: async ({
      ruleId,
      isActive,
    }: {
      ruleId: string;
      isActive: boolean;
    }): Promise<void> => {
      const { error } = await supabase
        .from('social_auto_reply_rules')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', ruleId);

      if (error) throw error;
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(
        isActive ? 'Rule activated' : 'Rule deactivated',
        { description: `Auto-reply rule has been ${isActive ? 'activated' : 'deactivated'}` }
      );
    },
    onError: (error: Error) => {
      toast.error('Failed to toggle rule', { description: error.message });
    },
  });

  // Wrapper functions
  const createRule = useCallback(
    async (request: CreateAutoReplyRuleRequest): Promise<SocialAutoReplyRule> => {
      return createRuleMutation.mutateAsync(request);
    },
    [createRuleMutation]
  );

  const updateRule = useCallback(
    async (ruleId: string, updates: Partial<SocialAutoReplyRule>): Promise<SocialAutoReplyRule> => {
      return updateRuleMutation.mutateAsync({ ruleId, updates });
    },
    [updateRuleMutation]
  );

  const deleteRule = useCallback(
    async (ruleId: string): Promise<void> => {
      return deleteRuleMutation.mutateAsync(ruleId);
    },
    [deleteRuleMutation]
  );

  const toggleRule = useCallback(
    async (ruleId: string, isActive: boolean): Promise<void> => {
      return toggleRuleMutation.mutateAsync({ ruleId, isActive });
    },
    [toggleRuleMutation]
  );

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    rules,
    isLoading,
    error: error as Error | null,
    createRule,
    updateRule,
    deleteRule,
    toggleRule,
    refresh,
  };
}
